import type { Guide } from '../types';
import { guide as assignTeamLeaderOnly } from './assignTeamLeaderOnly';
import { guide as unavailableBlocksAssignment } from './unavailableBlocksAssignment';
import { guide as tmsContactsAndCustomerTypes } from './tmsContactsAndCustomerTypes';
import { guide as preOrderArchive } from './preOrderArchive';
import { guide as intakeOrderCancellation } from './intakeOrderCancellation';
import { guide as subcontractorRuleOwnership } from './subcontractorRuleOwnership';
import { guide as orderChecklist } from './orderChecklist';
import { guide as orderHistoryCollapsed } from './orderHistoryCollapsed';
import { guide as requiredFieldsHighlight } from './requiredFieldsHighlight';
import { guide as intakeQuestionField } from './intakeQuestionField';
import { guide as usageTelemetry } from './usageTelemetry';
import { guide as guideOverlay } from './guideOverlay';
import { guide as roleCenter } from './roleCenter';
import { guide as fleetCorrections } from './fleetCorrections';
import { guide as parameterOwnership } from './parameterOwnership';
import { guide as orderChangeLeadAfterDeparture } from './orderChangeLeadAfterDeparture';
import { guide as subcontractorExceptionByCs } from './subcontractorExceptionByCs';
import { guide as teamLeaderBalance } from './teamLeaderBalance';
import { guide as bankRateTypeColumns } from './bankRateTypeColumns';
import { guide as carrierVerification } from './carrierVerification';
import { guide as namedNotifications } from './namedNotifications';
import { guide as notificationReadState } from './notificationReadState';
import { guide as tmsExportOneStep } from './tmsExportOneStep';
import { guide as viaDeliveryAutoRetry } from './viaDeliveryAutoRetry';
import { guide as orderChangeFieldOwner } from './orderChangeFieldOwner';
import { guide as preOrderLinkOtherContractor } from './preOrderLinkOtherContractor';
import { guide as preOrderAgreementSource } from './preOrderAgreementSource';
import { guide as csLight } from './csLight';
import { guide as carrierAcceptance } from './carrierAcceptance';
import { guide as marginFiveBands } from './marginFiveBands';
import { guide as publicationDataConfirmation } from './publicationDataConfirmation';
import { guide as tradeHandoverReasonOptional } from './tradeHandoverReasonOptional';
import { guide as cabotageByTms } from './cabotageByTms';
import { guide as variantWithinWindows } from './variantWithinWindows';
import { guide as loadingWindowEnd } from './loadingWindowEnd';
import { guide as assignOverrideWider } from './assignOverrideWider';
import { guide as regionDigitsByCountry } from './regionDigitsByCountry';
import { guide as ftlDecisionNotifications } from './ftlDecisionNotifications';
import { guide as trimbleTerminalOnCard } from './trimbleTerminalOnCard';
import { guide as intakeDepartments } from './intakeDepartments';
import { guide as intakeControlWindow } from './intakeControlWindow';
import { guide as intakeHandoff } from './intakeHandoff';
import { guide as publicationExceptionRequests } from './publicationExceptionRequests';
import { guide as fuelSurcharges } from './fuelSurcharges';
import { guide as publicationFleetVariantsWarning } from './publicationFleetVariantsWarning';
import { guide as tradeTransferBeforeDeparture } from './tradeTransferBeforeDeparture';
import { guide as vehicleChangeRequest } from './vehicleChangeRequest';
import { guide as orderChangeRegister } from './orderChangeRegister';
import { guide as preOrderContractor } from './preOrderContractor';
import { guide as unassignBeforeDeparture } from './unassignBeforeDeparture';
import { guide as variantValidity } from './variantValidity';
import { guide as assignButtonForPlanner } from './assignButtonForPlanner';
import { guide as variantNotifications } from './variantNotifications';
import { guide as plannerDashboard } from './plannerDashboard';
import { guide as teamLeaderDashboard } from './teamLeaderDashboard';
import { guide as ftlNotes } from './ftlNotes';
import { guide as tradeTransferReasons } from './tradeTransferReasons';
import { guide as preOrderCopy } from './preOrderCopy';
import { guide as preOrderStageInBank } from './preOrderStageInBank';
import { guide as tradeDashboard } from './tradeDashboard';
import { guide as carrierOfferRegister } from './carrierOfferRegister';
import { guide as ganttWindowBrackets } from './ganttWindowBrackets';
import { guide as runningOrderNotVariant } from './runningOrderNotVariant';
import { guide as staleTachographPast } from './staleTachographPast';

/**
 * Rejestr przewodników po zmianach (decyzja użytkownika 21.10.2026).
 *
 * Jeden plik na przełącznik: `tours/<klucz przełącznika>.ts` z eksportem
 * `guide`. Nakładka bierze listę stąd; nagrywarka slajdów
 * (`e2e/guides/record.mjs`) czyta te same pliki bezpośrednio, więc nowy
 * przewodnik trzeba dopisać i tu — test `guideRegistry.test.ts` pilnuje,
 * żeby lista i katalog się zgadzały. Kolejność = kolejność w „Co nowego”
 * (najnowsze funkcje na górze).
 */
export const GUIDES: readonly Guide[] = [
  // Wydanie 26.10.2026 — zmiany funkcji schowane za przełącznikami (S-26.10-01…03).
  assignTeamLeaderOnly,
  unavailableBlocksAssignment,
  tmsContactsAndCustomerTypes,
  // Prace 25.10.2026 — paczka CS i Administracja, agent A (S-25.10-01).
  preOrderArchive,
  // Prace 25.10.2026, CS i Administracja — anulowanie, reguła klienta (S-25.10-05, -06).
  intakeOrderCancellation,
  subcontractorRuleOwnership,
  // Prace 24.10.2026, G1 — okno zlecenia i przyjęcie (S-24.10-01…04).
  orderChecklist,
  orderHistoryCollapsed,
  requiredFieldsHighlight,
  intakeQuestionField,
  // Prace 24.10.2026, G2 — harmonogramy (S-24.10-05…07).
  ganttWindowBrackets,
  runningOrderNotVariant,
  staleTachographPast,
  // Prace 24.10.2026, G4 — pulpit Team Leadera: bilans, deficyt i nadwyżka (S-24.10-08).
  teamLeaderBalance,
  // Prace 24.10.2026, G4 — parametry i stawki kosztu z właścicielem i datą (S-24.10-12).
  parameterOwnership,
  orderChangeLeadAfterDeparture,
  subcontractorExceptionByCs,
  // Prace 24.10.2026, G3 — powiadomienia, Trade, bank (S-24.10-09…11, -13).
  notificationReadState,
  namedNotifications,
  carrierVerification,
  bankRateTypeColumns,
  // Prace 22.10.2026 — centrum ról i uprawnień (S-22.10-01).
  roleCenter,
  // Prace 22.10.2026 — pomiar użycia aplikacji (S-22.10-02).
  usageTelemetry,
  // Prace 22.10.2026 — korekty kartoteki pojazdu (S-22.10-03).
  fleetCorrections,
  // S-21.10-28 — przewodnik po zmianach.
  guideOverlay,
  // Paczka p. Mariusza 21.10, fala C — Administracja i CS (S-21.10-11…16).
  tmsExportOneStep,
  viaDeliveryAutoRetry,
  orderChangeFieldOwner,
  preOrderLinkOtherContractor,
  preOrderAgreementSource,
  csLight,
  // Fala C — Trade (S-21.10-17…20).
  carrierAcceptance,
  marginFiveBands,
  publicationDataConfirmation,
  tradeHandoverReasonOptional,
  // Fala C — FTL i rekomendacje (S-21.10-21…27).
  cabotageByTms,
  variantWithinWindows,
  loadingWindowEnd,
  assignOverrideWider,
  regionDigitsByCountry,
  ftlDecisionNotifications,
  trimbleTerminalOnCard,
  // Makieta back office v2, fala B (S-21.10-01…10).
  intakeDepartments,
  intakeControlWindow,
  intakeHandoff,
  publicationExceptionRequests,
  fuelSurcharges,
  publicationFleetVariantsWarning,
  tradeTransferBeforeDeparture,
  vehicleChangeRequest,
  orderChangeRegister,
  preOrderContractor,
  // Makieta back office v2, fala A (S-20.10-01…13; bez S-20.10-05 — migawka rekomendacji, niżej).
  unassignBeforeDeparture,
  variantValidity,
  assignButtonForPlanner,
  variantNotifications,
  plannerDashboard,
  teamLeaderDashboard,
  ftlNotes,
  tradeTransferReasons,
  preOrderCopy,
  preOrderStageInBank,
  tradeDashboard,
  carrierOfferRegister,
];
